import * as Sentry from '@sentry/nextjs';
import { formatUnits as etherFormatUnits } from 'ethers';
import moment from 'moment-timezone';

import { TimeLeftType } from '@/global/types/project.type';

interface WalletError extends Error {
  code?: number | string;
  reason?: string;
  shortMessage?: string;
  data?: {
    code?: number;
    message?: string;
  };
}

export function getGalaAddress(address: string): string {
  if (address.startsWith('client|')) {
    return address.replace('client|', '');
  }
  return address;
}

export function calculateTimeLeft(diff: number): TimeLeftType {
  const timeLeft: TimeLeftType = {
    days: Math.floor(diff / (1000 * 60 * 60 * 24)),
    hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
    minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
    seconds: Math.floor((diff % (1000 * 60)) / 1000),
  };

  return timeLeft;
}

function extractErrorMessage(error: unknown): string | null {
  if (error instanceof Error) {
    const message = error.message;
    // Check if message contains "Request Arguments"
    const index = message.indexOf('\nRequest Arguments');
    if (index !== -1) {
      // Extract everything before "Request Arguments"
      return message.substring(0, index).trim();
    }

    // Check if message contains "Details:"
    const detailsIndex = message.indexOf('\nDetails:');
    if (detailsIndex !== -1) {
      const detailsLine = message.substring(detailsIndex).split('\n')[1];
      if (detailsLine?.includes('MetaMask Tx Signature:')) {
        return detailsLine.replace('MetaMask Tx Signature:', '').trim();
      }
    }
  }
  return null;
}

function isUserRejectedError(error: unknown): boolean {
  if (typeof error === 'object' && error !== null) {
    const err = error as WalletError;
    const shortMessageIncludes = err.shortMessage ? err.shortMessage.includes('User rejected') : false;
    const messageIncludes = err.message
      ? err.message.includes('User denied transaction') || err.message.includes('User rejected')
      : false;
    const reasonIncludes = err.reason ? err.reason.includes('User rejected') : false;
    const dataMessageIncludes = err.data?.message ? err.data.message.includes('User rejected') : false;

    // Extract error message if other checks fail
    const extractedMessage = extractErrorMessage(error);
    const extractedMessageIncludes = extractedMessage
      ? extractedMessage.includes('User rejected') || extractedMessage.includes('User denied')
      : false;

    return (
      err.code === 4001 || // MetaMask user rejection code
      err.code === 'ACTION_REJECTED' ||
      shortMessageIncludes ||
      messageIncludes ||
      reasonIncludes ||
      dataMessageIncludes ||
      extractedMessageIncludes
    );
  }
  return false;
}

function getWalletErrorMessage(error: unknown): string {
  if (typeof error !== 'object' || error === null) {
    return 'Transaction failed, please try again';
  }

  const err = error as WalletError;

  // Extract error message first
  const extractedMessage = extractErrorMessage(error);
  if (extractedMessage) {
    return extractedMessage;
  }

  // Check for contract revert errors
  const errorString = JSON.stringify(error).toLowerCase();
  if (errorString.includes('fundisnotenough')) {
    return 'Insufficient funds in the contract';
  }

  // User rejected cases
  if (isUserRejectedError(err)) {
    return 'Transaction was rejected by user';
  }

  // Insufficient funds for gas
  const errorTexts = [err.message || '', err.reason || '', err.data?.message || ''];
  if (errorTexts.some(text => text.includes('insufficient funds'))) {
    return 'Insufficient funds for transaction';
  }

  // Contract execution errors
  if (err.message && err.message.includes('contract function "claimAll" reverted')) {
    // Extract the specific revert reason if available
    const revertMatch = err.message.match(/Error: ([A-Za-z]+)\(\)/);
    if (revertMatch && revertMatch[1]) {
      return revertMatch[1]; // This will return "FundIsNotEnough" or other revert reasons
    }
  }

  // Gas estimation errors
  if (
    err.message &&
    (err.message.includes('gas required exceeds allowance') || err.message.includes('insufficient gas'))
  ) {
    return 'Transaction would fail - check your gas settings';
  }

  // Network errors
  if (err.message && err.message.includes('network')) {
    return 'Network error occurred. Please check your connection and try again';
  }

  // Nonce errors
  if (err.message && err.message.includes('nonce')) {
    return 'Transaction nonce error. Please reset your wallet or try again';
  }

  return err.message || err.reason || err.data?.message || 'Transaction failed, please try again';
}

function sanitizeValue(value: any): any {
  if (typeof value === 'string') {
    // Sanitize potential PII or sensitive data patterns
    return value
      .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, '[REDACTED_EMAIL]') // Emails
      .replace(/\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/g, '[REDACTED_IP]');
  } else if (Array.isArray(value)) {
    return value.map(item => sanitizeValue(item));
  } else if (value && typeof value === 'object') {
    const sanitized: Record<string, any> = {};
    for (const [key, val] of Object.entries(value)) {
      // Skip known sensitive field names
      if (['privateKey', 'secret', 'password', 'token', 'apiKey'].includes(key.toLowerCase())) {
        sanitized[key] = '[REDACTED]';
      } else {
        sanitized[key] = sanitizeValue(val);
      }
    }
    return sanitized;
  }
  return value;
}

function enrichErrorForSentry(error: unknown, errorDetails: Record<string, any>, functionName: string) {
  // Sanitize error details before processing
  const sanitizedDetails = sanitizeValue(errorDetails);

  if (error instanceof Error) {
    // Sanitize error message if present
    const sanitizedError = new Error(sanitizeValue(error.message));
    sanitizedError.stack = error.stack; // Stack trace doesn't typically contain PII

    Sentry.withScope(scope => {
      // Add basic context
      scope.setTag('function.name', functionName);
      scope.setTag('error.source', 'node-sale-v2');

      // Add sanitized error details as extra context
      scope.setExtra('errorDetails', sanitizedDetails);
      scope.setExtra('timestamp', sanitizedDetails.timestamp);
      scope.setExtra('errorType', sanitizedDetails.type);
      scope.setExtra('functionName', functionName);

      // Add sanitized wallet specific details if they exist
      if (sanitizedDetails.code) scope.setExtra('errorCode', sanitizedDetails.code);
      if (sanitizedDetails.reason) scope.setExtra('errorReason', sanitizedDetails.reason);
      if (sanitizedDetails.shortMessage) scope.setExtra('shortMessage', sanitizedDetails.shortMessage);
      if (sanitizedDetails.extractedMessage) {
        scope.setExtra('extractedMessage', sanitizedDetails.extractedMessage);
      }

      // Enhanced error categorization
      const errorMessage = error.message?.toLowerCase() || '';
      const errorCategory = getErrorCategory(errorMessage);
      scope.setTag('error.category', errorCategory);

      // Add environment context
      scope.setTag('environment', process.env.NODE_ENV || 'development');

      // Capture the sanitized error with all the additional context
      Sentry.captureException(sanitizedError);
    });
  } else {
    // Handle non-Error objects
    Sentry.withScope(scope => {
      scope.setTag('function.name', functionName);
      scope.setTag('error.source', 'node-sale-v2');
      scope.setExtra('functionName', functionName);
      scope.setExtra('errorDetails', sanitizedDetails);
      scope.setLevel('error');
      Sentry.captureMessage('Non-Error object thrown', 'error');
    });
  }
}

// Helper function to determine error category
function getErrorCategory(errorMessage: string): string {
  const categories = [
    { pattern: 'user rejected', category: 'user_rejection' },
    { pattern: 'insufficient funds', category: 'insufficient_funds' },
    { pattern: 'gas', category: 'gas_error' },
    { pattern: 'network', category: 'network_error' },
    { pattern: 'nonce', category: 'nonce_error' },
    { pattern: 'rate limit', category: 'rate_limit' },
    { pattern: 'timeout', category: 'timeout' },
    { pattern: 'unauthorized', category: 'authorization' },
  ];

  for (const { pattern, category } of categories) {
    if (errorMessage.includes(pattern)) {
      return category;
    }
  }

  return 'other';
}

export const logError = (error: unknown, functionName: string) => {
  const errorMessage = getWalletErrorMessage(error);

  const errorDetails = {
    timestamp: new Date().toISOString(),
    type: error?.constructor?.name,
    message: errorMessage,
    originalMessage: error instanceof Error ? error.message : String(error),
    extractedMessage: extractErrorMessage(error),
    code: (error as WalletError)?.code,
    reason: (error as WalletError)?.reason,
    shortMessage: (error as WalletError)?.shortMessage,
    stack: error instanceof Error ? error.stack : undefined,
    details:
      typeof error === 'object'
        ? JSON.stringify(error, (key, value) => (typeof value === 'bigint' ? value?.toString() : value), 2)
        : undefined,
  };

  enrichErrorForSentry(error, errorDetails, functionName);

  return {
    ...errorDetails,
    message: errorMessage,
  };
};

export const convertToHex = (input: string) => {
  const bytes = new TextEncoder().encode(input);

  const hex = Array.from(bytes)
    .map(byte => byte.toString(16).padStart(2, '0'))
    .join('');

  return `0x${hex}`;
};

export function ensureHttps(url: string) {
  if (!url) return '';

  if (!url.startsWith('https://') && !url.startsWith('http://')) {
    return 'https://' + url;
  }

  return url;
}

export const mergeTimeDate = (date?: string, time?: string) => {
  if (!date || !time) return undefined;

  return moment(new Date(date));
};

export const formatUnits = (value?: bigint | string, decimals: number = 18): string => {
  if (!value || isNaN(Number(value.toString()))) return '0';
  return etherFormatUnits(value.toString(), decimals);
};
